import { Bell } from "lucide-react";
import { type ReactNode, useState } from "react";
import { TextButton } from "../actions/text-button";
import { useArcLink } from "../components/arc/lib/arc-provider";
import { Popover, PopoverContent, PopoverTrigger } from "../components/arc/popover/popover";
import { Switch } from "../components/arc/switch/switch";
import type { PushStatus } from "../data/push";
import styles from "./notifications.module.css";

/** One message in the person's inbox (`GET …/notifications`, P11-02). */
export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  /** Where it leads in the app, when it leads somewhere. */
  link: string | null;
  read_at: string | null;
  created_at: string;
}

/** "Notifications" for screen readers, with the unread count when there is one. */
export const notificationsLabel = (unread: number) => (unread ? `Notifications, ${unread} unread` : "Notifications");

/** The bell, with the design's red dot while anything is unread (the count is in the label). */
export function BellIcon({ unread }: { unread: number }) {
  return (
    <span className={styles.bell}>
      <Bell size={18} aria-hidden="true" />
      {unread ? <span className={styles.unreadDot} aria-hidden="true" /> : null}
    </span>
  );
}

export interface NotificationListProps {
  /** Undefined while the first page loads. */
  items: NotificationItem[] | undefined;
  /** Why they couldn't be loaded. */
  error?: string;
  /** Called as a row is opened, before its link is followed: mark it read. */
  onSelect: (item: NotificationItem) => void;
  /** The time each was sent, in the company's time zone. */
  formatTime: (iso: string) => string;
}

/** The inbox's rows, newest first. Unread ones carry a dot; a row with a link opens it. */
export function NotificationList({ items, error, onSelect, formatTime }: NotificationListProps) {
  const Link = useArcLink();
  if (error) return <p className={styles.state}>{error}</p>;
  if (!items) return <p className={styles.state}>Loading notifications…</p>;
  if (items.length === 0) return <p className={styles.state}>Nothing yet. New notifications show up here.</p>;
  return (
    <ul className={styles.list}>
      {items.map((item) => {
        const content = (
          <>
            <span className={styles.dot} data-unread={item.read_at ? undefined : ""} aria-hidden="true" />
            <span className={styles.text}>
              <span className={styles.title}>
                {item.read_at ? null : <span className={styles.srOnly}>Unread: </span>}
                {item.title}
              </span>
              <span className={styles.body}>{item.body}</span>
              <time className={styles.time} dateTime={item.created_at}>
                {formatTime(item.created_at)}
              </time>
            </span>
          </>
        );
        return (
          <li key={item.id}>
            {item.link ? (
              <Link href={item.link} className={styles.row} onClick={() => onSelect(item)}>
                {content}
              </Link>
            ) : (
              <button type="button" className={styles.row} onClick={() => onSelect(item)}>
                {content}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface NotificationBellProps extends Omit<NotificationListProps, "items"> {
  unread: number;
  /** The newest notifications; loaded when the bell is opened. */
  items: NotificationItem[] | undefined;
  onOpenChange?: (open: boolean) => void;
  /** Marks every notification read. */
  onReadAll: () => Promise<unknown>;
}

/**
 * The top bar's bell (P11-03): the unread count, and a panel with the newest notifications.
 * Opening one marks it read and follows its link; Mark all read clears the count.
 */
export function NotificationBell({
  unread,
  items,
  error,
  onOpenChange,
  onSelect,
  onReadAll,
  formatTime,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const change = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Popover open={open} onOpenChange={change}>
      <PopoverTrigger className={styles.trigger} aria-label={notificationsLabel(unread)}>
        <BellIcon unread={unread} />
      </PopoverTrigger>
      <PopoverContent align="end" className={styles.panel} aria-label="Notifications">
        <div className={styles.head}>
          <h2 className={styles.heading}>Notifications</h2>
          {unread ? (
            <TextButton
              disabled={reading}
              onClick={() => {
                setReading(true);
                void onReadAll().finally(() => setReading(false));
              }}
            >
              {reading ? "Marking…" : "Mark all read"}
            </TextButton>
          ) : null}
        </div>
        <div className={styles.scroll}>
          <NotificationList
            items={items}
            error={error}
            formatTime={formatTime}
            onSelect={(item) => {
              onSelect(item);
              if (item.link) change(false);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface PushToggleProps {
  status: PushStatus;
  busy: boolean;
  error: string | null;
  turnOn: () => Promise<unknown>;
  turnOff: () => Promise<unknown>;
  /** What turning it on gets you, under the switch. */
  description: string;
  /** Shown instead when this browser has no push (an iPhone outside the installed app). */
  unsupported?: ReactNode;
}

/**
 * "Notifications on this device" (P11-05): a switch that applies at once. The browser asks for
 * permission only after it's switched on. Nothing shows when the platform has no push keys.
 */
export function PushToggle({ status, busy, error, turnOn, turnOff, description, unsupported }: PushToggleProps) {
  if (status === "unavailable" || status === "checking") return null;
  if (status === "unsupported") return unsupported ? <div className={styles.push}>{unsupported}</div> : null;
  return (
    <div className={styles.push}>
      <div className={styles.pushRow}>
        <div className={styles.text}>
          <span className={styles.title} id="push-label">
            Notifications on this device
          </span>
          <span className={styles.pushNote}>
            {status === "blocked"
              ? "Blocked in this browser's settings for this site. Allow notifications there to turn them on."
              : description}
          </span>
        </div>
        <Switch
          aria-labelledby="push-label"
          checked={status === "on"}
          disabled={busy || status === "blocked"}
          onCheckedChange={(on) => void (on ? turnOn() : turnOff())}
        />
      </div>
      {error ? (
        <p className={styles.pushError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** One topic of `GET …/notifications/preferences` (P11-07). */
export interface NotificationTopic {
  topic: string;
  label: string;
  description: string;
  channels: { channel: "sms" | "webpush" | "fcm"; enabled: boolean; locked: boolean; note: string | null }[];
}

// fcm is the Fleet app's push: the web apps never list it, but the contract allows it
const CHANNEL_LABEL = { sms: "Text messages", webpush: "Push", fcm: "Push" } as const;

export interface NotificationSettingsProps {
  /** Undefined while they load. */
  topics: NotificationTopic[] | undefined;
  /** Saves one switch; the list re-renders from what the API answers. */
  onChange: (topic: string, channel: "sms" | "webpush" | "fcm", enabled: boolean) => Promise<unknown>;
  error?: string;
}

/**
 * What reaches me, per topic (P11-07): a switch per channel that applies at once. A locked one
 * (the order and payment texts) stays on, with its note saying why.
 */
export function NotificationSettings({ topics, onChange, error }: NotificationSettingsProps) {
  const [saving, setSaving] = useState<string | null>(null);
  if (!topics) return <p className={styles.state}>Loading your settings…</p>;
  return (
    <div className={styles.settings}>
      {error ? (
        <p className={styles.pushError} role="alert">
          {error}
        </p>
      ) : null}
      {topics.map((t) => (
        <section key={t.topic} className={styles.topic} aria-labelledby={`topic-${t.topic}`}>
          <div className={styles.text}>
            <h3 className={styles.title} id={`topic-${t.topic}`}>
              {t.label}
            </h3>
            <span className={styles.pushNote}>{t.description}</span>
          </div>
          {t.channels.map((c) => {
            const key = `${t.topic}:${c.channel}`;
            return (
              <div key={c.channel} className={styles.channel}>
                <div className={styles.text}>
                  <span>{CHANNEL_LABEL[c.channel]}</span>
                  {c.note ? <span className={styles.pushNote}>{c.note}</span> : null}
                </div>
                <Switch
                  aria-label={`${t.label}: ${CHANNEL_LABEL[c.channel]}`}
                  checked={c.enabled}
                  disabled={c.locked || saving === key}
                  onCheckedChange={(enabled) => {
                    setSaving(key);
                    void onChange(t.topic, c.channel, enabled).finally(() => setSaving(null));
                  }}
                />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
